import ky from 'ky';
import { ENV } from 'src/utils/env';
import { getAuthBearerToken } from 'src/utils/get-auth-with-bearer';
import { z } from 'zod';

export const SIM_CARD_DETAIL = z.object({
  sim_id: z.string(),
  lat: z.number(),
  lon: z.number(),
  not_found: z.boolean(),
  status: z.string().nullable(),
  rate_plan: z.string().nullable(),
  last_seen: z.string().nullable(),
  data_usage: z.number(),
});

const SIM_CARD_DETAIL_LIST = z.array(SIM_CARD_DETAIL);

export const SIM_CARD_DETAIL_RESPONSE = z.object({
  data: SIM_CARD_DETAIL_LIST,
  last_page: z.boolean(),
  total_pages: z.number(),
  metadata: z.object({
    plans: z.array(z.string()),
    total_data_usage: z.number(),
    statuses: z.array(z.string()),
    total_sims: z.number(),
    median_data_usage: z.number(),
    devices_above_median: z.number(),
  }),
});

export type SIM_CARD_DETAIL_RESPONSE_SCHEMA = z.infer<typeof SIM_CARD_DETAIL_RESPONSE>;

export type SIM_CARD_DETAIL_SCHEMA = z.infer<typeof SIM_CARD_DETAIL>;

const fetchSimCardDetailsPage = async (page: number, status?: string) => {
  const bearerToken = getAuthBearerToken();

  if (!bearerToken) {
    throw new Error('Failed to pull sim details');
  }

  const response = await ky
    .get(`${ENV.API_URL}/api/sim_detail/`, {
      headers: {
        Authorization: bearerToken,
      },
      searchParams: {
        page,
        status,
      },
    })
    .json();

  const result = SIM_CARD_DETAIL_RESPONSE.safeParse(response);

  if (!result.success) {
    console.error('failures', result.error);
    throw new Error('Failed to pull sim details');
  }

  return result.data;
};

// a single page of sims, along with the metadata for the table
export const getSimCardDetailsPagePromise = async ({ page, status = undefined }: { page: number; status?: string }) =>
  fetchSimCardDetailsPage(page, status);

// stop map loading too many sims at once
const MAX_SIMS = 1000;

// every sim across all pages, as one list up to MAX_SIMS
export const getAllSimCardDetailsPromise = async (status?: string) => {
  const firstPage = await fetchSimCardDetailsPage(1, status);
  const results = firstPage.data;

  let currentPage = 2;
  let lastPage = firstPage.last_page;

  while (!lastPage && results.length < MAX_SIMS) {
    // eslint-disable-next-line no-await-in-loop
    const nextPage = await fetchSimCardDetailsPage(currentPage, status);

    results.push(...nextPage.data);
    lastPage = nextPage.last_page;

    currentPage++;
  }

  return results;
};
